package com.gastoncantera.intercom

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.gastoncantera.intercom.auth.AuthRepository
import com.gastoncantera.intercom.call.CallManager
import com.gastoncantera.intercom.call.CallRepository
import com.gastoncantera.intercom.call.CallState
import com.gastoncantera.intercom.config.UsuarioMatch
import com.gastoncantera.intercom.presence.PresenceRepository
import com.gastoncantera.intercom.presence.signOutReleasingPush
import com.gastoncantera.intercom.push.MissedCalls
import com.gastoncantera.intercom.ui.CallAudioEffect
import com.gastoncantera.intercom.ui.KeepScreenOn
import com.gastoncantera.intercom.ui.LogoutConfirmDialog
import com.gastoncantera.intercom.ui.MissedCallsDialog
import com.gastoncantera.intercom.ui.CallErrorBanner
import com.gastoncantera.intercom.ui.CalleeActiveCall
import com.gastoncantera.intercom.ui.FullScreenCallBanner
import com.gastoncantera.intercom.ui.NotificationPermissionBanner
import com.gastoncantera.intercom.ui.ShowOverLockscreen
import com.gastoncantera.intercom.ui.rememberFullScreenCallPermissionAction
import com.gastoncantera.intercom.ui.rememberNotificationSetup
import dev.gitlive.firebase.auth.FirebaseUser
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import timbrebahia.shared.generated.resources.Res
import timbrebahia.shared.generated.resources.logo
import timbrebahia.shared.generated.resources.logout

@Composable
fun UsuarioScreen(
    user: FirebaseUser,
    matches: List<UsuarioMatch>,
    authRepository: AuthRepository,
    presenceRepository: PresenceRepository,
    callRepository: CallRepository,
) {
    val scope = rememberCoroutineScope()
    val uid = user.uid
    val callManager = remember { CallManager(callRepository, scope) }
    val callState by callManager.state.collectAsState()
    val callError by callManager.error.collectAsState()

    CallAudioEffect(active = callState == CallState.Calling || callState == CallState.Connected)
    // Solo durante llamada: el residente idle puede atenuar/bloquear normal.
    KeepScreenOn(
        active = callState == CallState.Incoming ||
            callState == CallState.Calling ||
            callState == CallState.Connected,
    )
    // Sobre el bloqueo solo mientras hay algo que atender: entrante (para ver Aceptar/Rechazar sin
    // desbloquear) y en curso (que el bloqueo no tape la llamada ya aceptada).
    ShowOverLockscreen(
        active = callState == CallState.Incoming || callState == CallState.Connected,
    )

    LaunchedEffect(uid, matches) {
        presenceRepository.register(uid, matches.map { it.callId })
        callManager.listenForIncoming(matches.map { it.callId })
    }

    // Notificaciones: SOLO en rol usuario. Pide permiso on-demand, persiste el token FCM y, si está
    // denegado, expone el cartel para activarlas. El portero nunca pasa por acá.
    val notificationPrompt = rememberNotificationSetup(uid, presenceRepository)
    // Solo Android 14+ sin el permiso de full-screen intent (típico al instalar desde Play Store).
    // Si el usuario no lo concede, la notificación de llamada igual funciona con acciones normales.
    val fullScreenCallAction = rememberFullScreenCallPermissionAction()

    var showMissedDialog by remember { mutableStateOf(false) }
    val popupTick by MissedCalls.popupTick.collectAsState()
    LaunchedEffect(popupTick) {
        if (MissedCalls.pending().isNotEmpty()) showMissedDialog = true
    }
    // Las perdidas no pueden taparle la llamada actual (al despertar por full-screen intent el popup
    // caía sobre la pantalla de atender): quedan pendientes y salen cuando la llamada termina.
    val noCallOnScreen = callState == CallState.Idle || callState == CallState.Ended
    if (showMissedDialog && noCallOnScreen && MissedCalls.pending().isNotEmpty()) {
        MissedCallsDialog(
            calls = MissedCalls.pending(),
            onDismiss = {
                showMissedDialog = false
                scope.launch { MissedCalls.clear() }
            },
        )
    }

    // El buzón (callId) identifica el depto exacto que llama.
    // y lo mantenemos hasta que termina, así el título no se pierde por timings de Firestore.
    val incomingCallId by callManager.incomingCallId.collectAsState()

    // El timbre lo corta CallManager en cada transición (accept / finish / answeredElsewhere), no la
    // UI: acá el primer valor de callState es Idle —el Incoming llega unos ms después, por Firestore—
    // y al abrir la app desde la notificación ese Idle inicial parecía "la llamada terminó" y mataba
    // el timbre del servicio a mitad de camino.

    var callerTitle by remember { mutableStateOf("Portero") }
    var callerDepto by remember { mutableStateOf("") }
    LaunchedEffect(callState, incomingCallId, matches) {
        when (callState) {
            CallState.Idle, CallState.Ended -> {
                callerTitle = "Portero"
                callerDepto = ""
            }
            else -> incomingCallId?.let { cid ->
                matches.firstOrNull { it.callId == cid }?.let {
                    callerTitle = it.formatBuilding()
                    callerDepto = it.depto
                }
            }
        }
    }

    val inCall = callState == CallState.Incoming ||
        callState == CallState.Connected ||
        callState == CallState.AnsweredElsewhere
    if (inCall) {
        CalleeActiveCall(
            title = callerTitle,
            subtitle = callerDepto,
            callManager = callManager,
            callState = callState,
            scope = scope,
        )
        return
    }

    // Se declara después del corte por llamada: si entra una mientras está abierto el diálogo, se
    // descarta con la composición y no reaparece al colgar.
    var showLogout by remember { mutableStateOf(false) }

    BoxWithConstraints(
        modifier = Modifier.fillMaxSize().windowInsetsPadding(WindowInsets.safeDrawing),
    ) {
        val screenHeight = maxHeight
        val density = LocalDensity.current
        // Alto real del pie (email + logout), medido: con un valor fijo o sobra scroll o se corta.
        var footerHeight by remember { mutableStateOf(0.dp) }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                // Mínimo alto = pantalla; si no entra, scroll.
                .heightIn(min = screenHeight)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    // Reserva justo el pie; centra el resto en lo que queda, sin scroll de más.
                    .heightIn(min = (screenHeight - footerHeight).coerceAtLeast(0.dp))
                    .padding(24.dp),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                BoxWithConstraints(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 32.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    val logoWidth = if (maxWidth <= 480.dp) maxWidth * 0.55f else 220.dp
                    Image(
                        painter = painterResource(Res.drawable.logo),
                        contentDescription = APP_DISPLAY_NAME,
                        modifier = Modifier.width(logoWidth).aspectRatio(560f / 531f),
                        contentScale = ContentScale.Fit,
                    )
                }

                callError?.let { err ->
                    CallErrorBanner(
                        err,
                        withVideo = false,
                        onResolved = { callManager.clearError() },
                        modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                    )
                }

                notificationPrompt?.let { prompt ->
                    NotificationPermissionBanner(
                        prompt,
                        modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                    )
                }

                // Solo si las notificaciones no están denegadas (evitamos apilar dos carteles de permisos).
                if (notificationPrompt == null) {
                    fullScreenCallAction?.let { enable ->
                        FullScreenCallBanner(
                            onEnable = enable,
                            modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                        )
                    }
                }

                Text(
                    "Esperando llamadas…",
                    style = MaterialTheme.typography.bodyMedium,
                )

                matches.forEach { match ->
                    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp)) {
                        Text(
                            match.consorcio,
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Bold,
                        )
                        Text(
                            if (match.torre != null) "Torre ${match.torre} · ${match.depto}" else match.depto,
                            style = MaterialTheme.typography.bodyMedium,
                            modifier = Modifier.padding(top = 2.dp),
                        )
                    }
                    HorizontalDivider()
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .onSizeChanged { footerHeight = with(density) { it.height.toDp() } }
                    .padding(bottom = 24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    "Usuario logueado",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    user.email ?: user.uid,
                    style = MaterialTheme.typography.bodyMedium,
                )
            }
        }

        // Arriba a la derecha y anclado (no scrollea con el contenido). Antes era un botón al pie,
        // justo donde queda el dedo después de Colgar: se cerraba sesión sin querer al cortar.
        IconButton(
            onClick = { showLogout = true },
            modifier = Modifier.align(Alignment.TopEnd).padding(8.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.logout),
                contentDescription = "Cerrar sesión",
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }

    if (showLogout) {
        LogoutConfirmDialog(
            message = "Vas a dejar de recibir llamadas en este dispositivo.",
            onDismiss = { showLogout = false },
            onConfirmed = {
                showLogout = false
                scope.launch {
                    callManager.hangup()
                    callManager.stopListening()
                    // Logout por-dispositivo: suelta solo el token de este equipo; otros
                    // dispositivos siguen recibiendo notificaciones.
                    signOutReleasingPush(authRepository, presenceRepository, uid)
                }
            },
        )
    }
}

private fun UsuarioMatch.formatBuilding(): String = buildString {
    append(consorcio)
    if (torre != null) append(" · Torre $torre")
}
